import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import Button from '../Button/Button';
import Menu from '../Menu/Menu';
import Menubar from '../Menubar/Menubar';
import SelectList from '../SelectList/SelectList';
import createFakeEvent from '../../lib/createFakeEvent';
import Gravity from '../../constants/Gravity';

/**
 * `Languagebar` is a container based on {@link Menubar} which displays a row of languages and functionality to add and
 * remove languages. The languages are an array of locales returned from the Vimond API and this component can be used when
 * you need to provide different options or fields per language.
 *
 * @property {string} addLanguageText The text of the **Add Language** button. Defaults to "+ Add Language".
 * @property {string} className The class name of this component. Will be appended to "core-languagebar".
 * @property {boolean} collapsable If set to `true`, the language bar items will be collapsed to a dropdown when there is not
 * enough room to display all items. This requires that the CSS styles the language bar in such a way that the items will not
 * overflow. Defaults to `true`.
 * @property {boolean} disabled If set to `true`, the user will only be allowed to select, add or delete languages. Defaults
 * to `false`.
 * @property {array} locales An array of locale objects returned from the Vimond API. Defaults to [].
 * @property {function} onAdd A callback that is called when the user has selected a new language drom the **+ Add Language**
 * dropdown.
 * @property {function} onChange A callback that is called when the user selects a language. The callback is passed an event
 * with the selected locale object set as the event's target value. The target value will be the same object as passed in the
 * *locales* array
 * @property {function} onRemove A callback that is called when the user removes a language. The callback is passed an event
 * with the selected locale object set as the event's target value, the index of the (not yet) removed language and the
 * locale ID of the locale that should be selected next if the requested locale is removed.
 * @property {string|object} selected The selected locale. Can be the locale ID or the entire locale object itself.
 * @property {boolean} showAddLanguage If set to `true`, the language bar will display a **+ Add Language** button that the
 * user can click to add more languages. Defaults to `true`.
 * @property {array} visible An array of the languages that should be visible. The component will filter the *locales* array
 * and list the rest of the available languages in the under the **+ Add Language** dropdown. Defaults to [].
 *
 * @see Menubar
 */
export default class Languagebar extends PureComponent {

  static displayName = 'Languagebar';

  static propTypes = {
    addLanguageText: PropTypes.string,
    className: PropTypes.string,
    collapsable: PropTypes.bool,
    disabled: PropTypes.bool,
    locales: PropTypes.array,
    onAdd: PropTypes.func,
    onChange: PropTypes.func,
    onRemove: PropTypes.func,
    selected: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object,
    ]),
    showAddLanguage: PropTypes.bool,
    visible: PropTypes.array,
  };

  static defaultProps = {
    addLanguageText: '+ Add language',
    collapsable: true,
    locales: [],
    showAddLanguage: true,
    visible: [],
  };

  onAdd = e => {
    if (this.props.onAdd) {
      this.props.onAdd(createFakeEvent(e.target.value), this.props.visible.length);
    }
  };

  onChange = e => {
    if (this.props.onChange) {
      const localeId = e.target.getAttribute('data-locale-id');
      const locale = this.props.visible.find(item => item.localeId === localeId);

      this.props.onChange(createFakeEvent(locale));
    }
  };

  onRemove = (e, index) => {
    if (this.props.onRemove) {
      const { visible } = this.props;
      const nextSelectedIndex = Math.min(this.getSelectedIndex(), visible.length - 2);
      const tmp = visible.slice();

      tmp.splice(index, 1);
      this.props.onRemove(createFakeEvent(this.props.visible[index]), index, tmp[nextSelectedIndex].localeId);
    }
  };

  getSelectedIndex = () => {
    const { selected, visible } = this.props;

    if (typeof selected === 'string') {
      return visible.findIndex(locale => locale.localeId === selected);
    }
    return visible.indexOf(selected);
  };

  render() {
    const { addLanguageText, className, collapsable, disabled, locales = [], showAddLanguage, visible } = this.props;
    const remainingLocales = locales
      .filter(locale => !visible.find(loc => locale.localeId === loc.localeId))
      .sort((a, b) => a.language.localeCompare(b.language));
    const selectedIndex = this.getSelectedIndex();
    let add;

    const items = visible.map((locale, index) =>
      <Button className={cx({ active: index === selectedIndex })} data-locale-id={locale.localeId} key={index}>
        {locale.language}
      </Button>);
    if (showAddLanguage && remainingLocales.length) {
      add = (
        <Menu title={addLanguageText} gravity={Gravity.SOUTH} arrowSize={-4} className="add" customContent={true} offset={7} disabled={disabled}>
          <SelectList className="core-languagebar-languages" options={remainingLocales} labelKey="language" valueKey="localeId" onChange={this.onAdd}/>
        </Menu>
      );
    }
    return (
      <div className={cx('core-languagebar', className)}>
        <div className="separator"/>
        <div className="languages">
          <Menubar editable={!disabled} allowRemoveLast={false} onRemove={this.onRemove} selected={selectedIndex} showUnderline
            onClick={this.onChange} collapsable={collapsable}>
            {items}
          </Menubar>
          {add}
        </div>
      </div>
    );
  }

}
